/**
 * Number agreement for word problems.
 *
 * The story templates are written in the plural - "{name} had {variable1}
 * apples" - because that is how they read for almost every value. When the
 * number that lands there is 1 they read "1 apples", which happened on roughly
 * one word problem in five once the low digits were selected.
 *
 * The vocabulary is a closed set: every noun that can follow a placeholder is
 * already in the template files, and a test asserts this module handles all of
 * them. That is what makes rewriting them at substitution time safe, rather
 * than needing markup in two hundred templates.
 */

/** Plurals that do not follow a rule. */
const IRREGULAR: Record<string, string> = {
    leaves: "leaf",
    // Singulars that genuinely end in s, which no rule can tell from "vases".
    buses: "bus",
    glasses: "glass",
    dresses: "dress",
    shelves: "shelf",
    snowmen: "snowman",
    children: "child",
    people: "person",
    feet: "foot",
    teeth: "tooth",
    mice: "mouse",
    geese: "goose",
    // Singulars that end in -ie. The -ies rule below turns -ies into -y, which
    // is right for "candies" and wrong for these ("cookies" read "1 cooky").
    // Changing what a seeded sheet prints is ADR 0009's business; ADR 0080
    // records why this one was changed anyway.
    cookies: "cookie",
    brownies: "brownie",
    movies: "movie",
    zombies: "zombie",
    calories: "calorie",
    smoothies: "smoothie",
    hoodies: "hoodie",
    selfies: "selfie",
    // Unchanged in the singular.
    fish: "fish",
    sheep: "sheep",
};

/**
 * Words that end in s but are already singular, so must never be trimmed.
 * "a class" would otherwise become "a clas".
 */
const NOT_PLURAL = new Set([
    "class",
    "glass",
    "grass",
    "dress",
    "bus",
    "is",
    "has",
    "was",
    "its",
    "his",
    "less",
    "across",
    "this",
    "plus",
]);

/** True when this word is a plural form the singulariser can handle. */
export function isPlural(word: string): boolean {
    const lower = word.toLowerCase();
    if (NOT_PLURAL.has(lower)) {
        return false;
    }
    if (lower in IRREGULAR) {
        return true;
    }
    return lower.endsWith("s") && lower.length > 2;
}

/** The singular of a plural noun. Assumes isPlural() said yes. */
export function singular(word: string): string {
    const lower = word.toLowerCase();

    if (lower in IRREGULAR) {
        return IRREGULAR[lower];
    }
    // berries -> berry, but not boys -> boy handled by the plain rule below.
    if (lower.endsWith("ies") && lower.length > 4) {
        return lower.slice(0, -3) + "y";
    }
    // boxes, bunches, sandwiches, tomatoes -> box, bunch, sandwich, tomato.
    // `s` is deliberately not in this list: "vases" is vase + s, not vas + es,
    // and only a word whose singular really ends in s (bus, glass) takes -ses.
    // Those are handled as irregulars rather than guessed at.
    if (/(?:ch|sh|x|z|o)es$/.test(lower)) {
        return lower.slice(0, -2);
    }
    return lower.slice(0, -1);
}

/**
 * Makes the noun after a number agree with it.
 *
 * Scans forward from where the number was substituted and singularises the
 * first plural word it finds, so modifiers are stepped over without needing to
 * be listed: "1 toy boats" becomes "1 toy boat", "1 more stars" becomes
 * "1 more star", "1 bright red tomatoes" becomes "1 bright red tomato".
 *
 * The scan stops at the end of the clause or at the next placeholder, so a
 * number never reaches past its own noun and change one belonging to another.
 */
export function agreeAfterNumber(text: string, value: number, placeholder: string): string {
    if (value !== 1) {
        return text;
    }

    const at = text.indexOf(placeholder);
    if (at < 0) {
        return text;
    }

    const start = at + placeholder.length;
    // Stop before the next placeholder, or at the end of the clause.
    const rest = text.slice(start);
    const boundary = rest.search(/[.?!;]|\{/);
    const scanEnd = boundary < 0 ? rest.length : boundary;

    const window = rest.slice(0, scanEnd);
    const match = [...window.matchAll(/[A-Za-z]+/g)].find((m) => isPlural(m[0]));
    if (!match) {
        return text;
    }

    const wordStart = start + match.index!;
    const wordEnd = wordStart + match[0].length;
    const singularised =
        text.slice(0, wordStart) + singular(match[0]) + text.slice(wordEnd);

    // A pronoun later in the story refers back to that noun, so it has to
    // follow it down: "had 1 apple and shared them" reads "shared it".
    return agreePronoun(singularised, wordEnd);
}

/**
 * Makes a pronoun referring back to a just-singularised noun agree with it.
 *
 * Only "them" occurs in the story files, and it always refers to the first
 * noun - the collection being counted, shared out or grouped.
 */
function agreePronoun(text: string, from: number): string {
    const head = text.slice(0, from);
    const tail = text.slice(from).replace(/\bthem\b/g, "it");
    return head + tail;
}
